// src/student/pages/StudentDashboard.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  CheckSquare,
  PenTool,
  FileText,
  Calendar,
  ArrowRight,
  TrendingUp,
  Clock,
  Sparkles,
} from "lucide-react";
import { useStudentAuth } from "../context/StudentAuthContext";
import studentPortalAPI from "../services/studentApi";

const StudentDashboard = () => {
  const { studentUser } = useStudentAuth();
  const [stats, setStats] = useState({
    prelimsCount: 0,
    mainsCount: 0,
    dailyCount: 0,
    monthlyCount: 0,
    avgScore: 0,
  });
  const [recentTests, setRecentTests] = useState([]);
  const [latestDailyCA, setLatestDailyCA] = useState(null);
  const [latestMonthlyCA, setLatestMonthlyCA] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const [testsRes, dailyRes, monthlyRes, resultsRes] = await Promise.allSettled([
          studentPortalAPI.getPublishedTests(),
          studentPortalAPI.getDailyCA({ limit: 5 }),
          studentPortalAPI.getMonthlyCA({ limit: 2 }),
          studentPortalAPI.getMyResults(studentUser?.email),
        ]);

        const publishedTests = testsRes.status === "fulfilled" ? testsRes.value?.data || [] : [];
        const dailyItems = dailyRes.status === "fulfilled" ? dailyRes.value?.data || [] : [];
        const monthlyItems = monthlyRes.status === "fulfilled" ? monthlyRes.value?.data || [] : [];
        const prelimsResults = resultsRes.status === "fulfilled" ? resultsRes.value?.prelimsResults || [] : [];
        const mainsResults = resultsRes.status === "fulfilled" ? resultsRes.value?.mainsResults || [] : [];

        setRecentTests(publishedTests.slice(0, 3));
        if (dailyItems.length > 0) setLatestDailyCA(dailyItems[0]);
        if (monthlyItems.length > 0) setLatestMonthlyCA(monthlyItems[0]);

        const totalPrelimsMarks = prelimsResults.reduce((acc, curr) => acc + (curr.marksObtained || 0), 0);
        const avg = prelimsResults.length > 0 ? (totalPrelimsMarks / prelimsResults.length).toFixed(1) : 0;

        setStats({
          prelimsCount: prelimsResults.length,
          mainsCount: mainsResults.length,
          dailyCount: dailyItems.length,
          monthlyCount: monthlyItems.length,
          avgScore: avg,
        });
      } catch (e) {
        console.warn("Error loading student dashboard:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [studentUser?.email]);

  if (loading) {
    return (
      <div className="py-24 text-center space-y-3">
        <div className="w-10 h-10 border-4 border-red-900 border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="text-sm font-semibold text-slate-600">Loading Aspirant Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Hero Banner */}
      <div className="bg-gradient-to-r from-[#7b0000] via-red-900 to-[#0b1528] rounded-3xl p-6 sm:p-8 text-white shadow-md relative overflow-hidden">
        <div className="relative z-10 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-300 bg-white/10 px-3 py-1 rounded-full mb-3 backdrop-blur-xs">
            <Sparkles size={12} /> UPSC Civil Services Aspirant Portal
          </span>
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            Welcome back, {studentUser?.name || "Aspirant"}!
          </h2>
          <p className="text-white/80 text-xs sm:text-sm mt-2 leading-relaxed">
            Access free daily news analysis, monthly compendiums, full-length UPSC Prelims mock tests, and faculty-evaluated Daily Answer Writing (DAW).
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/student/prelims"
              className="px-4 py-2 bg-amber-400 hover:bg-amber-300 text-slate-900 text-xs font-bold rounded-xl shadow-xs transition-colors flex items-center gap-1.5"
            >
              <CheckSquare size={14} /> Take Prelims Mock
            </Link>
            <Link
              to="/student/mains"
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-xl border border-white/20 transition-colors flex items-center gap-1.5"
            >
              <PenTool size={14} /> Submit Mains Answer
            </Link>
          </div>
        </div>
      </div>

      {/* 4 Performance & Material Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-semibold">Prelims Tests Taken</span>
            <CheckSquare size={17} className="text-red-800" />
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900">{stats.prelimsCount}</span>
            <span className="text-[11px] text-slate-400">Tests completed</span>
          </div>
          <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1">
            <TrendingUp size={12} className="text-emerald-600" /> Avg score: {stats.avgScore} pts
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-semibold">Mains Submissions</span>
            <PenTool size={17} className="text-amber-700" />
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900">{stats.mainsCount}</span>
            <span className="text-[11px] text-slate-400">Answers submitted</span>
          </div>
          <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1">
            <Clock size={12} className="text-blue-600" /> Faculty feedback attached
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-semibold">Daily Current Affairs</span>
            <FileText size={17} className="text-blue-700" />
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900">{stats.dailyCount}</span>
            <span className="text-[11px] text-slate-400">Recent digests</span>
          </div>
          <Link
            to="/student/daily-ca"
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-red-900 hover:underline"
          >
            Read today's CA <ArrowRight size={11} />
          </Link>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs">
          <div className="flex items-center justify-between text-slate-500">
            <span className="text-xs font-semibold">Monthly Magazines</span>
            <Calendar size={17} className="text-purple-700" />
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900">{stats.monthlyCount}</span>
            <span className="text-[11px] text-slate-400">Compendiums</span>
          </div>
          <Link
            to="/student/monthly-ca"
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-red-900 hover:underline"
          >
            Download compendiums <ArrowRight size={11} />
          </Link>
        </div>
      </div>

      {/* Two Column Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Published Prelims Tests Ready to Attempt */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-2xs p-5">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <CheckSquare size={16} className="text-red-900" /> Available Prelims Mock Tests
            </h3>
            <Link
              to="/student/prelims"
              className="text-xs font-semibold text-red-900 hover:underline"
            >
              View All
            </Link>
          </div>

          <div className="mt-4 space-y-3">
            {recentTests.length === 0 ? (
              <p className="text-xs text-slate-500 py-4 text-center">
                No active tests published at the moment. Please check back soon.
              </p>
            ) : (
              recentTests.map((t) => (
                <div
                  key={t._id || t.id}
                  className="p-3.5 bg-slate-50 border border-slate-200/70 rounded-xl flex items-center justify-between hover:bg-slate-100/70 transition-colors"
                >
                  <div className="min-w-0 pr-3">
                    <h4 className="text-xs font-bold text-slate-900 truncate">
                      {t.title}
                    </h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {t.questions?.length || t.totalQuestions || 0} Questions • {t.durationMinutes || 120} Mins • Total {t.totalMarks || 200} Marks
                    </p>
                  </div>
                  <Link
                    to={`/student/prelims/test/${t._id || t.id}`}
                    className="flex-shrink-0 px-3 py-1.5 bg-red-900 hover:bg-red-800 text-white text-xs font-bold rounded-lg shadow-2xs"
                  >
                    Start Test
                  </Link>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right: Latest Current Affairs Highlights */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-2xs p-5 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <FileText size={16} className="text-blue-900" /> Latest Current Affairs Release
            </h3>
            <Link
              to="/student/daily-ca"
              className="text-xs font-semibold text-red-900 hover:underline"
            >
              Archive
            </Link>
          </div>

          {latestDailyCA ? (
            <div className="p-4 bg-red-50/50 border border-red-100 rounded-xl space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-wider text-red-800 bg-red-100 px-2 py-0.5 rounded-full">
                  {latestDailyCA.category || "Daily CA"}
                </span>
                <span className="text-[11px] text-slate-500">
                  {new Date(latestDailyCA.publicationDate || latestDailyCA.createdAt).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              </div>
              <h4 className="text-xs sm:text-sm font-bold text-slate-900">
                {latestDailyCA.title}
              </h4>
              <p className="text-xs text-slate-600 line-clamp-2">
                {latestDailyCA.description || "In-depth editorial summary and key prelims pointers."}
              </p>
              <div className="pt-2 flex items-center gap-3">
                {latestDailyCA.pdfUrl && (
                  <a
                    href={latestDailyCA.pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-bold text-red-900 hover:underline"
                  >
                    View Document (PDF) &rarr;
                  </a>
                )}
              </div>
            </div>
          ) : (
            <p className="text-xs text-slate-500 py-4 text-center">
              No daily current affairs published yet.
            </p>
          )}

          {latestMonthlyCA && (
            <div className="p-3 bg-blue-50/50 border border-blue-100 rounded-xl flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold text-blue-800 uppercase tracking-wide">
                  Monthly Compendium
                </span>
                <h5 className="text-xs font-bold text-slate-900">
                  {latestMonthlyCA.month} {latestMonthlyCA.year} Edition
                </h5>
              </div>
              {latestMonthlyCA.pdfUrl && (
                <a
                  href={latestMonthlyCA.pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 bg-blue-900 hover:bg-blue-800 text-white text-xs font-bold rounded-lg"
                >
                  Download PDF
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
